import { forwardRef, useCallback, useEffect, useRef, useState, type PropsWithChildren } from "react"

import { Box, Stack } from "@mui/material"
import type { EmblaCarouselType } from "embla-carousel"
import useEmblaCarousel from "embla-carousel-react"
import type { FC, ResponsiveImageType } from ".."
import { Container, ResponsiveImage, IconButton, SvgIcon, Link } from ".."

type FeaturedLogoType = {
  icon: ResponsiveImageType
  link?: { uri: string; title: string }
}

type FeaturedLogosType = {
  items: FeaturedLogoType[]
}

const FeaturedLogos: FC<FeaturedLogosType> = ({ items }) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const scrollTrackRef = useRef<HTMLDivElement>(null)
  const scrollThumbRef = useRef<HTMLDivElement>(null)
  const [thumbWidth, setThumbWidth] = useState(20)
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([])

  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: "start",
    containScroll: "trimSnaps",
    breakpoints: {
      // 1110px = hardcoded value from the design
      "(min-width: 900)": {
        active: false,
      },
    },
  })

  const onInit = useCallback(
    (emblaApi: EmblaCarouselType) => {
      const snaps = emblaApi.scrollSnapList()
      setScrollSnaps(snaps)

      if (containerRef.current && scrollTrackRef.current) {
        const { clientWidth, scrollWidth } = containerRef.current
        const { clientWidth: trackSize } = scrollTrackRef.current
        setThumbWidth(Math.max((clientWidth / scrollWidth) * trackSize, 20))
      }
    },
    [containerRef, scrollTrackRef]
  )

  const onScroll = useCallback(
    (emblaApi: EmblaCarouselType) => {
      if (!scrollTrackRef.current || !scrollThumbRef.current) return

      const progress = emblaApi.scrollProgress()
      const { clientWidth: trackWidth } = scrollTrackRef.current
      const scrollX = progress * (trackWidth - thumbWidth)
      const thumb = scrollThumbRef.current
      thumb.style.left = `${scrollX}px`
    },
    [thumbWidth]
  )

  const handleTrackClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()

      const { current: trackCurrent } = scrollTrackRef
      const { current: contentCurrent } = containerRef

      if (!emblaApi || !trackCurrent || !contentCurrent) return

      const { clientX } = e
      const target = e.target as HTMLDivElement
      const rect = target.getBoundingClientRect()
      const trackLeft = rect.left
      const clickRatio = (clientX - trackLeft) / trackCurrent.clientWidth

      const closestSlide = scrollSnaps.reduce((prev, curr) => {
        return Math.abs(curr - clickRatio) < Math.abs(prev - clickRatio) ? curr : prev
      }, 0)
      const closestIndex = scrollSnaps.indexOf(closestSlide)
      emblaApi?.scrollTo(closestIndex)
    },
    [scrollSnaps, scrollTrackRef, containerRef]
  )

  useEffect(() => {
    if (!emblaApi) return

    onInit(emblaApi)
    emblaApi.on("reInit", onInit)
    emblaApi.on("resize", onInit)
    emblaApi.on("scroll", onScroll)
  }, [emblaApi, containerRef, scrollTrackRef, onInit, onScroll])

  return (
    <Container>
      <Box overflow="hidden" py={{ xs: 25, md: 40 }}>
        <Box
          ref={emblaRef}
          sx={(theme) => ({
            [theme.breakpoints.down(1110)]: {
              "cursor": "grab",

              "&:active": {
                cursor: "grabbing",
              },
            },
          })}
        >
          <FeaturedLogoItem ref={containerRef}>
            {items?.map((item, i) => (
              <Stack
                key={i}
                position="relative"
                alignItems="center"
                justifyContent="center"
                height={{ xs: 78, lg: 120, xl: 180 }}
                borderRadius={1.5}
                sx={(theme) => ({
                  "transition": (theme) =>
                    theme.transitions.create("box-shadow", {
                      duration: theme.transitions.duration.standard,
                    }),
                  "&:hover": {
                    boxShadow: "0px 0px 20px 0px rgba(0, 0, 0, 0.08)",
                    button: { opacity: 1, bottom: -12 },
                  },
                  [theme.breakpoints.down("lg")]: {
                    pointerEvents: "none",
                  },
                })}
              >
                {item.link && (
                  <Link
                    type="overlay"
                    href={item.link.uri}
                    aria-label={item.link.title}
                    title={item.link.title}
                  />
                )}
                <ResponsiveImage
                  image={item.icon}
                  maxHeight={{ xs: 40, lg: 62 }}
                  maxWidth={{ xs: 100, lg: 150 }}
                />
                {item.link && <FeaturedLogoLinkIcon />}
              </Stack>
            ))}
          </FeaturedLogoItem>
        </Box>

        <Box
          width="100%"
          height="2px"
          mt={5}
          position="relative"
          sx={(theme) => ({
            [theme.breakpoints.up(900)]: {
              display: "none",
            },
          })}
        >
          <Box
            ref={scrollTrackRef}
            onClick={handleTrackClick}
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            bgcolor={(theme) => theme.palette["neutral.200"].main}
            sx={{ "&:hover": { cursor: "pointer" } }}
          />
          <Box
            ref={scrollThumbRef}
            position="absolute"
            width={thumbWidth}
            top={0}
            left={0}
            bottom={0}
            bgcolor={(theme) => theme.palette["primary.800"].main}
          />
        </Box>
      </Box>
    </Container>
  )
}
export default FeaturedLogos

/** A sub-component for an individual Featured Logo Item */
const FeaturedLogoItem = forwardRef(({ children }: PropsWithChildren, ref) => (
  <Box
    ref={ref}
    display="grid"
    px={3}
    columnGap={{ xs: 3, md: 5 }}
    gridAutoFlow="column"
    gridAutoColumns="minmax(115px, auto)"
  >
    {children}
  </Box>
))
FeaturedLogoItem.displayName = "FeaturedLogoItem"

/** A sub-component for the Staff Card Icon */
const FeaturedLogoLinkIcon: FC = () => {
  return (
    <Box
      sx={(theme) => ({
        "display": "flex",
        "borderRadius": "100px",
        "background": theme.palette["primary.800"].main,
        "&:hover": { cursor: "pointer" },
        [theme.breakpoints.down("md")]: {
          display: "none",
        },
      })}
    >
      <IconButton
        variant="quaternary"
        icon={
          <SvgIcon
            variant="arrowOutward"
            size={18}
            sx={{
              fill: (theme) => theme.palette["neutral.100"].main,
            }}
          />
        }
        sx={{
          transition: ".3s",
          opacity: 0,
          position: "absolute",
          bottom: 0,
          right: -12,
        }}
      />
    </Box>
  )
}
